import React from "react";
import { Helmet } from "react-helmet-async";

export default function FixHub() {
  return (
    <>
      <Helmet>
        <title>DTF Troubleshooting Guide | DTF Problems, Fixes, and What to Check</title>
        <meta
          name="description"
          content="Having DTF printing problems? Explore common DTF issues like peeling, white ink failure, banding, static, ghosting, and more—plus what to check before wasting more prints."
        />
      </Helmet>

      <div className="px-4 py-10 max-w-4xl mx-auto text-white">
        <h1 className="text-3xl font-bold mb-6 text-center">
          DTF Troubleshooting Guide
        </h1>

        <p className="text-gray-300 mb-6 text-lg text-center">
          If your DTF prints are failing and you do not know why, you are not
          alone. Most DTF problems are caused by a handful of overlapping issues,
          but figuring out which one is affecting your setup can waste a lot of
          film, ink, powder, shirts, and time.
        </p>

        <p className="text-gray-400 mb-10 text-center">
          Start with the issue that matches your symptom most closely. Each page
          breaks down common causes, what to check first, and how to stop
          guessing.
        </p>

        <div className="mb-10 p-6 rounded-xl bg-[rgba(20,30,50,0.6)] border border-blue-500/20">
          <h2 className="text-xl font-semibold mb-3">
            Still Not Sure Which DTF Problem You Have?
          </h2>

          <p className="text-gray-300 mb-4">
            A lot of DTF issues look the same on the surface. What seems like a
            press problem might actually start with curing, white ink, static,
            film behavior, or printer setup.
          </p>

          <p className="text-gray-400 mb-4 italic">
            DTF Wizard helps you narrow down the actual cause so you can fix the
            right issue the first time.
          </p>

          <a
            href="https://dtfwizardapp.com"
            className="inline-block px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition text-white font-semibold"
          >
            Get Access to DTF Wizard
          </a>
        </div>

        <h2 className="text-xl font-semibold mb-4">Most Common DTF Issues</h2>
        <ul className="space-y-3 text-blue-400 mb-10">
          <li>
            <a href="/fix/dtf-print-not-sticking" className="hover:underline">
              DTF Print Not Sticking
            </a>
          </li>
          <li>
            <a href="/fix/dtf-peeling-after-wash" className="hover:underline">
              DTF Peeling After Wash
            </a>
          </li>
          <li>
            <a href="/fix/dtf-white-ink-not-printing" className="hover:underline">
              DTF White Ink Not Printing
            </a>
          </li>
          <li>
            <a href="/fix/dtf-banding-lines" className="hover:underline">
              DTF Banding Lines
            </a>
          </li>
          <li>
            <a href="/fix/dtf-powder-not-curing" className="hover:underline">
              DTF Powder Not Curing
            </a>
          </li>
        </ul>

        <h2 className="text-xl font-semibold mb-4">Print Quality Issues</h2>
        <ul className="space-y-3 text-blue-400 mb-10">
          <li>
            <a href="/fix/dtf-colors-dull" className="hover:underline">
              DTF Colors Dull or Faded
            </a>
          </li>
          <li>
            <a href="/fix/dtf-blurry-prints" className="hover:underline">
              DTF Blurry Prints
            </a>
          </li>
          <li>
            <a href="/fix/dtf-ghosting" className="hover:underline">
              DTF Ghosting
            </a>
          </li>
          <li>
            <a href="/fix/dtf-underbase-misalignment" className="hover:underline">
              DTF Underbase Misalignment
            </a>
          </li>
        </ul>

        <h2 className="text-xl font-semibold mb-4">Process and Material Issues</h2>
        <ul className="space-y-3 text-blue-400 mb-10">
          <li>
            <a href="/fix/dtf-ink-smearing" className="hover:underline">
              DTF Ink Smearing
            </a>
          </li>
          <li>
            <a href="/fix/dtf-film-not-releasing" className="hover:underline">
              DTF Film Not Releasing
            </a>
          </li>
          <li>
            <a href="/fix/dtf-cracking-after-pressing" className="hover:underline">
              DTF Cracking After Pressing
            </a>
          </li>
          <li>
            <a href="/fix/dtf-uneven-powder" className="hover:underline">
              DTF Uneven Powder
            </a>
          </li>
          <li>
            <a href="/fix/dtf-static-issues" className="hover:underline">
              DTF Static Issues
            </a>
          </li>
          <li>
            <a href="/fix/dtf-adhesion-issues" className="hover:underline">
              DTF Adhesion Issues
            </a>
          </li>
        </ul>

        <div className="mt-10 p-6 rounded-xl bg-[rgba(20,30,50,0.6)] border border-blue-500/20">
          <h2 className="text-xl font-semibold mb-3">
            Stop Guessing and Start Troubleshooting the Right Way
          </h2>

          <p className="text-gray-300 mb-4">
            These pages are a strong starting point, but many DTF problems have
            multiple possible causes. That is why so many print operators lose
            time chasing the wrong issue.
          </p>

          <p className="text-gray-400 mb-4 italic">
            DTF Wizard walks you through your exact setup so you can find the
            real problem faster.
          </p>

          <a
            href="https://dtfwizardapp.com"
            className="inline-block px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 transition text-white font-semibold"
          >
            Get Access to DTF Wizard
          </a>
        </div>
      </div>
    </>
  );
}